import { Eye, FileText, Info, Layers3, ListTree, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { Button } from "../../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../ui/dialog";
import { ScrollArea } from "../../ui/scroll-area";
import { Badge } from "../../ui/badge";
import { Input } from "../../ui/input";

interface DetailsDialogProps {
  data: any;
  title?: string;
  trigger?: "default" | "icon";
  testId?: string;
}

interface DetailEntry {
  section: string;
  label: string;
  value: any;
  searchText: string;
}

function formatKey(key: string): string {
  return key
    .replace(/\[(\d+)\]/g, " $1")
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}

function isPrimitive(value: any) {
  return value === null || value === undefined || ["boolean", "number", "string"].includes(typeof value);
}

function isPrimitiveArray(value: any[]) {
  return value.length === 0 || value.every((item) => isPrimitive(item));
}

function sectionFor(path: string[]) {
  return path.length > 1 ? formatKey(path[0]) : "General";
}

function labelFor(path: string[]) {
  const labelPath = path.length > 1 ? path.slice(1) : path;
  return labelPath.map(formatKey).join(" / ") || "Value";
}

function flattenObject(obj: any, path: string[] = []): DetailEntry[] {
  if (isPrimitive(obj) || Array.isArray(obj)) {
    return [{
      section: sectionFor(path),
      label: labelFor(path),
      value: obj,
      searchText: `${sectionFor(path)} ${labelFor(path)} ${String(obj ?? "")}`.toLowerCase(),
    }];
  }

  const result: DetailEntry[] = [];

  for (const [key, value] of Object.entries(obj || {})) {
    const nextPath = [...path, key];

    if (isPrimitive(value)) {
      result.push({
        section: sectionFor(nextPath),
        label: labelFor(nextPath),
        value,
        searchText: `${sectionFor(nextPath)} ${labelFor(nextPath)} ${String(value ?? "")}`.toLowerCase(),
      });
    } else if (Array.isArray(value)) {
      if (isPrimitiveArray(value)) {
        result.push({
          section: sectionFor(nextPath),
          label: labelFor(nextPath),
          value,
          searchText: `${sectionFor(nextPath)} ${labelFor(nextPath)} ${value.join(" ")}`.toLowerCase(),
        });
      } else {
        value.forEach((item, idx) => {
          result.push(...flattenObject(item, [...nextPath, `Item ${idx + 1}`]));
        });
      }
    } else {
      result.push(...flattenObject(value, nextPath));
    }
  }

  return result;
}

function isEmptyValue(value: any) {
  return value === null || value === undefined || value === "" || (Array.isArray(value) && value.length === 0);
}

function renderValue(value: any) {
  if (isEmptyValue(value)) {
    return <span className="text-[13px] font-medium text-muted-foreground">-</span>;
  }

  if (typeof value === "boolean") {
    return (
      <Badge
        variant="outline"
        className={`h-6 rounded-full px-2.5 text-[11px] font-semibold ${
          value
            ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-300"
            : "border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-800 dark:bg-slate-900/60 dark:text-slate-300"
        }`}
      >
        {value ? "True" : "False"}
      </Badge>
    );
  }

  if (typeof value === "number") {
    return <span className="text-[13px] font-semibold tabular-nums text-foreground">{value}</span>;
  }

  if (typeof value === "string") {
    const longValue = value.length > 80;
    return (
      <span className={`${longValue ? "font-mono text-[12px]" : "text-[13px]"} font-medium break-all text-foreground`}>
        {value}
      </span>
    );
  }

  if (Array.isArray(value)) {
    return (
      <div className="flex flex-wrap gap-1.5">
        {value.map((item, idx) => (
          <Badge key={idx} variant="secondary" className="h-6 rounded-full px-2.5 text-[11px] font-medium">
            {String(item)}
          </Badge>
        ))}
      </div>
    );
  }

  return <span className="text-[13px] font-medium break-all text-foreground">{String(value)}</span>;
}

function pickPrimaryValue(data: any, title: string) {
  return data?.email || data?.name || data?.displayName || data?.roleName || data?.id || title;
}

function pickSubtitle(data: any) {
  return data?.managedType || data?.type || data?.status || data?.riskChangeCategory || "Structured entity details";
}

export function DetailsDialog({ data, title = "Details", trigger = "default", testId = "button-details" }: DetailsDialogProps) {
  const [searchQuery, setSearchQuery] = useState("");

  const flattenedData = useMemo(() => flattenObject(data), [data]);
  const filteredData = useMemo(() => {
    if (!searchQuery) return flattenedData;
    const query = searchQuery.toLowerCase();
    return flattenedData.filter((item) => item.searchText.includes(query));
  }, [flattenedData, searchQuery]);

  const groupedData = useMemo(() => {
    return filteredData.reduce<Record<string, DetailEntry[]>>((groups, item) => {
      groups[item.section] = groups[item.section] || [];
      groups[item.section].push(item);
      return groups;
    }, {});
  }, [filteredData]);

  const populatedCount = flattenedData.filter((item) => !isEmptyValue(item.value)).length;
  const listCount = flattenedData.filter((item) => Array.isArray(item.value) && item.value.length > 0).length;
  const sectionCount = Object.keys(groupedData).length || 0;
  const primaryValue = pickPrimaryValue(data, title);
  const subtitle = pickSubtitle(data);

  return (
    <Dialog>
      <DialogTrigger asChild>
        {trigger === "icon" ? (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 flex-shrink-0"
            data-testid={testId}
          >
            <Info className="w-3 h-3" />
          </Button>
        ) : (
          <Button variant="outline" size="sm" className="gap-2" data-testid={testId}>
            <Eye className="w-4 h-4" />
            Details
          </Button>
        )}
      </DialogTrigger>

      <DialogContent className="max-w-5xl max-h-[86vh] overflow-hidden rounded-2xl border-border/70 p-0">
        <DialogHeader className="border-b border-border bg-card px-6 py-5">
          <DialogTitle className="flex items-start gap-4">
            <div className="mt-0.5 flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-sm">
              <FileText className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-lg font-semibold text-foreground">{title}</div>
              <DialogDescription className="mt-1 truncate text-xs font-medium text-muted-foreground">
                {primaryValue}
              </DialogDescription>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <Badge variant="secondary" className="rounded-full px-2.5 text-[11px]">
                  {subtitle}
                </Badge>
                <Badge variant="outline" className="rounded-full px-2.5 text-[11px]">
                  {populatedCount}/{flattenedData.length} populated
                </Badge>
              </div>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="border-b bg-muted/20 px-6 py-4">
          <div className="grid gap-3 md:grid-cols-[1fr_auto] md:items-center">
            <div className="relative">
              <Input
                type="text"
                placeholder="Search fields or values..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-10 rounded-full border-border/70 bg-background pl-9 text-sm"
                data-testid="input-search-details"
              />
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-xl border bg-background px-3 py-2 text-center">
                <div className="flex items-center justify-center gap-1 text-[10px] font-semibold uppercase text-muted-foreground">
                  <FileText className="h-3 w-3" /> Fields
                </div>
                <div className="mt-1 text-sm font-bold">{flattenedData.length}</div>
              </div>
              <div className="rounded-xl border bg-background px-3 py-2 text-center">
                <div className="flex items-center justify-center gap-1 text-[10px] font-semibold uppercase text-muted-foreground">
                  <Layers3 className="h-3 w-3" /> Sections
                </div>
                <div className="mt-1 text-sm font-bold">{sectionCount}</div>
              </div>
              <div className="rounded-xl border bg-background px-3 py-2 text-center">
                <div className="flex items-center justify-center gap-1 text-[10px] font-semibold uppercase text-muted-foreground">
                  <ListTree className="h-3 w-3" /> Lists
                </div>
                <div className="mt-1 text-sm font-bold">{listCount}</div>
              </div>
            </div>
          </div>
        </div>

        <ScrollArea className="max-h-[calc(86vh-230px)]">
          <div className="space-y-4 p-6">
            {Object.entries(groupedData).map(([section, items]) => (
              <section key={section} className="rounded-2xl border border-border bg-card p-4 shadow-sm">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <h3 className="text-sm font-semibold text-foreground">{section}</h3>
                  <Badge variant="secondary" className="rounded-full text-[10px]">
                    {items.length} fields
                  </Badge>
                </div>
                <div className="grid gap-2 md:grid-cols-2">
                  {items.map((item, idx) => (
                    <div key={`${item.label}-${idx}`} className="min-w-0 rounded-xl border border-border bg-card px-3 py-2.5">
                      <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                        {item.label}
                      </div>
                      <div className="min-w-0">{renderValue(item.value)}</div>
                    </div>
                  ))}
                </div>
              </section>
            ))}

            {filteredData.length === 0 && (
              <div className="rounded-2xl border border-dashed py-12 text-center">
                <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-muted">
                  <Search className="h-5 w-5 text-muted-foreground" />
                </div>
                <div className="text-sm font-semibold">No matching fields</div>
                <div className="mt-1 text-xs text-muted-foreground">Try another search term.</div>
              </div>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
